import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import { validateEmail } from '@utils/validate';
import { authApi, UserUrls } from '@api/user';
import { useAlert } from '@hooks/useAlert';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';
import { Button, TextField } from '@components/design-system';

interface ForgotPassDialogProps {
    open: boolean;
    onClose: () => void;
}

export const ForgotPassDialog = ({ open, onClose }: ForgotPassDialogProps) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | undefined>('');
    const [loading, setLoading] = useState(false);

    const { setAlert } = useAlert();

    const updateEmail = (newEmail: string) => {
        const { message } = validateEmail(newEmail);
        setError(message);
        setEmail(newEmail);
    };

    const handleClose = () => {
        onClose();
        setEmail('');
        setError('');
    };

    const onReset = async () => {
        setLoading(true);
        try {
            const resp = await authApi.get(UserUrls.ResetPass, { params: { email } }).catch(() => {
                setAlert('Could not send reset link, contact customer service if the issue persists.', 'error');
            });
            if (resp && resp.status === 200) {
                setAlert('Successfully sent reset link, check your inbox.', 'success');
            } else {
                setAlert('Could not send reset link, contact customer service if the issue persists.', 'error');
            }
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            maxWidth='xs'
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: `${radii.lg}px`,
                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
                    overflow: 'hidden',
                },
            }}>
            {/* Close button */}
            <IconButton
                onClick={handleClose}
                disabled={loading}
                sx={{
                    position: 'absolute',
                    right: 12,
                    top: 12,
                    color: colors.neutral[400],
                    '&:hover': {
                        color: colors.neutral[600],
                        backgroundColor: colors.neutral[100],
                    },
                }}>
                <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <DialogTitle sx={{ pt: 4, pb: 1, px: 3, textAlign: 'center' }}>
                {/* Icon */}
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: colors.primary[50],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: colors.primary[500],
                    }}>
                    <EmailOutlinedIcon sx={{ fontSize: 32 }} />
                </Box>

                <Typography
                    sx={{
                        fontFamily: typographyTokens.fontFamily.display,
                        fontWeight: typographyTokens.fontWeight.bold,
                        fontSize: typographyTokens.fontSize.xl,
                        color: colors.neutral[900],
                    }}>
                    Reset Password
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 2, textAlign: 'center' }}>
                <Typography
                    sx={{
                        color: colors.neutral[600],
                        fontSize: typographyTokens.fontSize.base,
                        lineHeight: 1.6,
                        mb: 3,
                    }}>
                    Enter your email and we'll send you a link to reset your password.
                </Typography>

                <TextField
                    label='Email address'
                    value={email}
                    onChange={updateEmail}
                    type='email'
                    placeholder='Enter your email'
                    error={Boolean(error)}
                    errorMessage={error}
                    startAdornment={<EmailOutlinedIcon sx={{ fontSize: 18, color: colors.neutral[400] }} />}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 1,
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'center',
                }}>
                <Button variant='secondary' onClick={handleClose} disabled={loading} fullWidth>
                    Cancel
                </Button>
                <Button
                    variant='primary'
                    onClick={onReset}
                    loading={loading}
                    disabled={Boolean(error) || !email}
                    fullWidth>
                    Send Reset
                </Button>
            </DialogActions>
        </Dialog>
    );
};
